import React, { useState, useEffect, useRef } from "react";
import { Input, Button } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { UploadCloud, Check } from "lucide-react";
import {
  _update_ambassador_data,
  _upload_image,
  _get_ambassador_agreement_pdf,
} from "../../common/axios_services";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../common/utils/notification";
import AmbassadorActionPlan from "./AmbassadorActionPlan";

const GRADUATION_YEARS = ["2027", "2028", "2029", "2030", "2031", "2032"];

const AmbassadorProfileEditor = ({ userProfile, schoolsList = [], onUpdated }) => {
  const pictureInputRef = useRef(null);
  const idCardInputRef = useRef(null);
  const [phoneNumber, setPhoneNumber] = useState(userProfile?.phoneNumber || "");
  const [whatsappNumber, setWhatsappNumber] = useState(
    userProfile?.ambassadorWhatsapp || userProfile?.phoneNumber || ""
  );
  const [isStudent, setIsStudent] = useState(Boolean(userProfile?.isStudent));
  const [school, setSchool] = useState(userProfile?.school || "");
  const [graduationYear, setGraduationYear] = useState(
    userProfile?.graduationYear || ""
  );
  const [studentNumber, setStudentNumber] = useState(
    userProfile?.studentNumber || ""
  );
  const [recentPictureUrl, setRecentPictureUrl] = useState(
    userProfile?.recentPictureUrl || ""
  );
  const [studentIdCardUrl, setStudentIdCardUrl] = useState(
    userProfile?.studentIdCardUrl || ""
  );
  const [igUrl, setIgUrl] = useState(
    userProfile?.ambassadorIgUrl || userProfile?.igUrl || ""
  );
  const [tiktokUrl, setTiktokUrl] = useState(
    userProfile?.ambassadorTiktokUrl || userProfile?.tiktokUrl || ""
  );
  const [facebookUrl, setFacebookUrl] = useState(
    userProfile?.ambassadorFacebookUrl || userProfile?.facebookUrl || ""
  );
  const [followerRange, setFollowerRange] = useState(
    userProfile?.ambassadorFollowersRange || userProfile?.followersRange || ""
  );
  const [topPosts, setTopPosts] = useState(
    Array.isArray(userProfile?.ambassadorTopPosts || userProfile?.topPosts) &&
      (userProfile?.ambassadorTopPosts || userProfile?.topPosts).length > 0
      ? userProfile?.ambassadorTopPosts || userProfile?.topPosts
      : [""]
  );

  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [uploadingIdCard, setUploadingIdCard] = useState(false);
  const [savingData, setSavingData] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (userProfile) {
      if (userProfile.phoneNumber) {
        setPhoneNumber(userProfile.phoneNumber);
      }
      if (userProfile.ambassadorWhatsapp || userProfile.phoneNumber) {
        setWhatsappNumber(userProfile.ambassadorWhatsapp || userProfile.phoneNumber);
      }
      const isStud =
        userProfile.isStudent !== undefined && userProfile.isStudent !== null
          ? (userProfile.isStudent === true || String(userProfile.isStudent) === "1" || String(userProfile.isStudent) === "true")
          : (userProfile.ambassadorIsStudent !== undefined && userProfile.ambassadorIsStudent !== null
            ? (userProfile.ambassadorIsStudent === true || String(userProfile.ambassadorIsStudent) === "1" || String(userProfile.ambassadorIsStudent) === "true")
            : Boolean(userProfile.school || userProfile.ambassadorSchool));

      setIsStudent(Boolean(isStud));
      const schoolVal = userProfile.school || userProfile.ambassadorSchool;
      if (schoolVal) setSchool(schoolVal);
      const gradYear = userProfile.graduationYear || userProfile.ambassadorGraduationYear;
      if (gradYear) setGraduationYear(String(gradYear));
      const studNum = userProfile.studentNumber || userProfile.ambassadorStudentNumber;
      if (studNum) setStudentNumber(String(studNum));
      const picUrl = userProfile.recentPictureUrl || userProfile.ambassadorRecentPictureUrl;
      if (picUrl) setRecentPictureUrl(picUrl);
      const idCardUrl = userProfile.studentIdCardUrl || userProfile.ambassadorStudentIdCardUrl;
      if (idCardUrl) setStudentIdCardUrl(idCardUrl);
      if (userProfile.ambassadorIgUrl || userProfile.igUrl) {
        setIgUrl(userProfile.ambassadorIgUrl || userProfile.igUrl);
      }
      if (userProfile.ambassadorTiktokUrl || userProfile.tiktokUrl) {
        setTiktokUrl(userProfile.ambassadorTiktokUrl || userProfile.tiktokUrl);
      }
      if (userProfile.ambassadorFacebookUrl || userProfile.facebookUrl) {
        setFacebookUrl(userProfile.ambassadorFacebookUrl || userProfile.facebookUrl);
      }
      if (userProfile.ambassadorFollowersRange || userProfile.followersRange) {
        setFollowerRange(userProfile.ambassadorFollowersRange || userProfile.followersRange);
      }
      const posts = userProfile.ambassadorTopPosts || userProfile.topPosts;
      if (Array.isArray(posts) && posts.length > 0) {
        setTopPosts(posts);
      }
    }
  }, [userProfile, schoolsList]);

  const handleFileUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (type === "picture") setUploadingPicture(true);
    if (type === "idcard") setUploadingIdCard(true);

    try {
      const res = await _upload_image(file);
      const url =
        res.data?.url ||
        res.data?.data?.url ||
        res.data?.imageUrl ||
        (typeof res.data === "string" ? res.data : "");
      if (url) {
        if (type === "picture") setRecentPictureUrl(url);
        if (type === "idcard") setStudentIdCardUrl(url);
        showSuccessNotification("File uploaded successfully!");
      } else {
        showErrorNotification("Failed to upload file.");
      }
    } catch (err) {
      showErrorNotification(err.response?.data?.message || "Upload failed.");
    } finally {
      if (type === "picture") setUploadingPicture(false);
      if (type === "idcard") setUploadingIdCard(false);
    }
  };

  const handlePostLinkChange = (index, value) => {
    const updated = [...topPosts];
    updated[index] = value;
    setTopPosts(updated);
  };

  const handleAddPostLink = () => {
    if (topPosts.length < 5) {
      setTopPosts([...topPosts, ""]);
    }
  };

  const handleRemovePostLink = (index) => {
    if (topPosts.length > 1) {
      setTopPosts(topPosts.filter((_, i) => i !== index));
    }
  };

  const handleDownloadAgreement = async () => {
    try {
      setDownloadingPdf(true);
      const res = await _get_ambassador_agreement_pdf();
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Konnect_Ambassador_Agreement.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showErrorNotification("Failed to download Ambassador Agreement PDF.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleSaveData = async () => {
    setSavingData(true);
    try {
      const cleanPosts = topPosts.filter((p) => p && p.trim() !== "");
      await _update_ambassador_data({
        phoneNumber,
        whatsappNumber,
        isStudent,
        school: isStudent ? school : null,
        graduationYear: isStudent ? graduationYear : null,
        studentNumber: isStudent ? studentNumber : null,
        recentPictureUrl: isStudent ? recentPictureUrl : null,
        studentIdCardUrl: isStudent ? studentIdCardUrl : null,
        igUrl,
        tiktokUrl,
        facebookUrl,
        followersRange: followerRange,
        topPosts: cleanPosts,
      });
      showSuccessNotification("Ambassador profile updated successfully!");
      if (onUpdated) onUpdated();
    } catch (err) {
      showErrorNotification(
        err.response?.data?.message || "Failed to update profile data."
      );
    } finally {
      setSavingData(false);
    }
  };

  return (
    <div className="space-y-6 pt-2">
      <div className="bg-slate-100 rounded-3xl p-6 md:p-8 text-center flex flex-col items-center relative overflow-hidden border border-emerald-900/40 shadow-sm">
        <div className="mb-4 bg-emerald-100 text-emerald-700 p-4 rounded-2xl border border-emerald-200 inline-block shadow-xs">
          <span className="text-4xl">👑</span>
        </div>
        <h2 className="font-gilroyBold text-3xl md:text-4xl text-gray-900 tracking-tight mb-2">
          Official Ambassador Portal Member
        </h2>
        <p className="text-gray-600 text-lg mb-6 max-w-xl leading-relaxed">
          Thank you for being a key partner in growing the Konnect ecosystem.
        </p>

        <div className="w-full bg-white p-5 rounded-2xl border border-gray-200/80 text-left mb-2 space-y-3 shadow-xs">
          <div className="flex justify-between items-center border-b border-gray-100 pb-3">
            <span className="text-lg text-gray-600">WhatsApp Contact</span>
            <span className="text-lg font-gilroyBold text-gray-900">
              {userProfile?.ambassadorWhatsapp || userProfile?.phoneNumber || "N/A"}
            </span>
          </div>
          <div className="flex justify-between items-center border-b border-gray-100 pb-3">
            <span className="text-lg text-gray-600">Member Since</span>
            <span className="text-lg font-gilroyBold text-emerald-700">
              {userProfile?.ambassadorApprovedAt
                ? new Date(userProfile.ambassadorApprovedAt).toLocaleDateString()
                : "Active"}
            </span>
          </div>
          <div className="flex justify-between items-center pt-1">
            <span className="text-lg text-gray-600">Official Contract</span>
            <button
              type="button"
              onClick={handleDownloadAgreement}
              disabled={downloadingPdf}
              className="text-lg font-gilroyBold text-emerald-700 hover:text-emerald-600 underline cursor-pointer disabled:opacity-50 transition-colors"
            >
              {downloadingPdf ? "Generating..." : "Download Official Agreement PDF"}
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-6" id="ambassador-profile-data-form">
        <div className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 shadow-sm text-left space-y-5">
          <div className="border-b border-gray-100 pb-3">
            <h3 className="font-gilroyBold text-2xl text-gray-900 m-0">
              Contact & Student Information
            </h3>
            <p className="text-gray-500 text-base m-0 mt-1">
              Update your primary WhatsApp contact number and student status.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-lg font-gilroyBold text-gray-900 mb-2">
                Telephone Number <span className="text-red-500">*</span>
              </label>
              <Input
                size="large"
                placeholder="e.g. 08012345678"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="rounded-2xl h-14 text-base font-medium border-gray-200"
              />
            </div>

            <div>
              <label className="block text-lg font-gilroyBold text-gray-900 mb-2">
                WhatsApp Number <span className="text-red-500">*</span>
              </label>
              <Input
                size="large"
                placeholder="e.g. 08012345678"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                className="rounded-2xl h-14 text-base font-medium border-gray-200"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <input
              type="checkbox"
              id="isStudentCheck"
              checked={isStudent}
              onChange={(e) => setIsStudent(e.target.checked)}
              className="w-5 h-5 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500 cursor-pointer"
            />
            <label
              htmlFor="isStudentCheck"
              className="text-lg font-gilroyBold text-gray-900 cursor-pointer"
            >
              I am currently a Student Ambassador
            </label>
          </div>

          {isStudent && (
            <div className="bg-gray-50/80 border border-gray-200/80 rounded-2xl p-6 space-y-4">
              <div className="flex items-center gap-2 pb-1">
                <div className="bg-emerald-900 text-white w-7 h-7 rounded-lg flex items-center justify-center text-xs">
                  🎓
                </div>
                <h4 className="font-gilroyBold text-base text-gray-900 uppercase tracking-wide m-0">
                  Student Identification Details
                </h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-lg font-gilroyBold text-gray-900 mb-1.5">
                    Higher Institution <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={school}
                    onChange={(e) => setSchool(e.target.value)}
                    className="w-full h-14 rounded-2xl border-2 border-gray-200 px-4 font-gilroyBold text-base bg-white focus:border-emerald-500 outline-none transition-all text-gray-900"
                  >
                    <option value="">Select institution...</option>
                    {school && !schoolsList.includes(school) && school !== "Other" && (
                      <option key={school} value={school}>
                        {school}
                      </option>
                    )}
                    {schoolsList
                      .filter((s) => s !== "Other")
                      .map((s, idx) => (
                        <option key={idx} value={s}>
                          {s}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="block text-lg font-gilroyBold text-gray-900 mb-1.5">
                    Graduation Year <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={String(graduationYear || "")}
                    onChange={(e) => setGraduationYear(e.target.value)}
                    className="w-full h-14 rounded-2xl border-2 border-gray-200 px-4 font-gilroyBold text-base bg-white focus:border-emerald-500 outline-none transition-all text-gray-900"
                  >
                    <option value="">Select year...</option>
                    {graduationYear && !GRADUATION_YEARS.includes(String(graduationYear)) && (
                      <option key={graduationYear} value={String(graduationYear)}>
                        {graduationYear}
                      </option>
                    )}
                    {GRADUATION_YEARS.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-lg font-gilroyBold text-gray-900 mb-1.5">
                    Student ID / Matric No
                  </label>
                  <Input
                    size="large"
                    placeholder="e.g. MAT/2023/1049"
                    value={studentNumber}
                    onChange={(e) => setStudentNumber(e.target.value)}
                    className="rounded-2xl h-14 text-base font-medium border-gray-200"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div>
                  <label className="block text-lg font-gilroyBold text-gray-900 mb-1.5">
                    Recent Headshot Photo
                  </label>
                  <input
                    type="file"
                    ref={pictureInputRef}
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, "picture")}
                    className="hidden"
                  />
                  {recentPictureUrl ? (
                    <div className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center justify-between gap-3 shadow-xs">
                      <div className="flex items-center gap-3 min-w-0">
                        <img
                          src={recentPictureUrl}
                          alt="Headshot"
                          className="w-14 h-14 object-cover rounded-xl border border-gray-200 shadow-xs flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <span className="font-gilroyBold text-sm text-gray-900 block truncate">
                            Headshot uploaded
                          </span>
                          <span className="font-gilroyBold text-xs text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full inline-block mt-1">
                            ✓ Verified headshot
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => pictureInputRef.current?.click()}
                        className="text-xs font-gilroyBold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 py-2 rounded-xl border border-emerald-200 cursor-pointer transition-all flex-shrink-0"
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => pictureInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-200 hover:border-emerald-500 bg-white rounded-2xl p-5 text-center cursor-pointer transition-all flex flex-col items-center justify-center gap-1.5"
                    >
                      <UploadCloud className="w-7 h-7 text-emerald-600 mb-0.5" />
                      <span className="font-gilroyBold text-base text-gray-800">
                        {uploadingPicture ? "Uploading photo..." : "Click to upload headshot photo"}
                      </span>
                      <span className="text-xs text-gray-500">JPG or PNG up to 5MB</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-lg font-gilroyBold text-gray-900 mb-1.5">
                    Student ID Card Image
                  </label>
                  <input
                    type="file"
                    ref={idCardInputRef}
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, "idcard")}
                    className="hidden"
                  />
                  {studentIdCardUrl ? (
                    <div className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center justify-between gap-3 shadow-xs">
                      <div className="flex items-center gap-3 min-w-0">
                        <img
                          src={studentIdCardUrl}
                          alt="ID Card"
                          className="w-16 h-12 object-cover rounded-xl border border-gray-200 shadow-xs flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <span className="font-gilroyBold text-sm text-gray-900 block truncate">
                            Student ID Card
                          </span>
                          <span className="font-gilroyBold text-xs text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full inline-block mt-1">
                            ✓ Verified ID card
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => idCardInputRef.current?.click()}
                        className="text-xs font-gilroyBold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 py-2 rounded-xl border border-emerald-200 cursor-pointer transition-all flex-shrink-0"
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => idCardInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-200 hover:border-emerald-500 bg-white rounded-2xl p-5 text-center cursor-pointer transition-all flex flex-col items-center justify-center gap-1.5"
                    >
                      <UploadCloud className="w-7 h-7 text-emerald-600 mb-0.5" />
                      <span className="font-gilroyBold text-base text-gray-800">
                        {uploadingIdCard ? "Uploading ID card..." : "Click to upload student ID card"}
                      </span>
                      <span className="text-xs text-gray-500">JPG or PNG up to 5MB</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 shadow-sm text-left space-y-5">
          <div className="border-b border-gray-100 pb-3">
            <h3 className="font-gilroyBold text-2xl text-gray-900 m-0">
              Social Media Handles & Profiles
            </h3>
            <p className="text-gray-500 text-[13px] m-0 mt-1">
              Provide links to your primary social media profiles.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-[13px] font-gilroyBold text-gray-700 mb-1">
                Instagram Profile URL
              </label>
              <Input
                size="large"
                placeholder="https://www.instagram.com/yourprofile"
                value={igUrl}
                onChange={(e) => setIgUrl(e.target.value)}
                className="rounded-2xl h-14 text-base font-medium border-gray-200"
              />
            </div>

            <div>
              <label className="block text-[13px] font-gilroyBold text-gray-700 mb-1">
                TikTok Profile URL
              </label>
              <Input
                size="large"
                placeholder="https://www.tiktok.com/@yourprofile"
                value={tiktokUrl}
                onChange={(e) => setTiktokUrl(e.target.value)}
                className="rounded-2xl h-14 text-base font-medium border-gray-200"
              />
            </div>

            <div>
              <label className="block text-[13px] font-gilroyBold text-gray-700 mb-1">
                Facebook Profile / Page URL
              </label>
              <Input
                size="large"
                placeholder="https://www.facebook.com/yourprofile"
                value={facebookUrl}
                onChange={(e) => setFacebookUrl(e.target.value)}
                className="rounded-2xl h-14 text-base font-medium border-gray-200"
              />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 shadow-sm text-left space-y-5">
          <div className="border-b border-gray-100 pb-3">
            <h3 className="font-gilroyBold text-2xl text-gray-900 m-0">
              Audience Reach & Top Content
            </h3>
            <p className="text-gray-500 text-[13px] m-0 mt-1">
              Update your total follower count and top performing post links.
            </p>
          </div>

          <div className="space-y-2">
            <label className="block text-lg font-gilroyBold text-gray-900 m-0">
              Total Followers Range Across Accounts <span className="text-red-500">*</span>
            </label>
            <select
              value={followerRange || ""}
              onChange={(e) => setFollowerRange(e.target.value)}
              className="w-full h-14 rounded-2xl font-gilroyBold text-[13px] px-4 border-2 border-gray-200 bg-white text-gray-900 focus:border-emerald-500 outline-none transition-all"
            >
              <option value="">Select range...</option>
              <option value="1000-3000">1,000 - 3,000 followers</option>
              <option value="3000-6000">3,000 - 6,000 followers</option>
              <option value="6000+">6,000+ followers</option>
            </select>
          </div>

          <div className="space-y-3">
            <label className="block text-lg font-gilroyBold text-gray-900 m-0">
              Top Performing Content / Post Links
            </label>

            <div className="space-y-3">
              {topPosts.map((link, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-[13px] font-gilroyBold text-gray-600 w-20 flex-shrink-0">
                    Post #{idx + 1}
                  </span>
                  <Input
                    size="large"
                    placeholder="https://..."
                    value={link}
                    onChange={(e) => handlePostLinkChange(idx, e.target.value)}
                    className="rounded-2xl h-14 text-base font-medium flex-1 border-gray-200"
                  />
                  {topPosts.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemovePostLink(idx)}
                      className="p-3 text-red-500 hover:bg-red-50 rounded-2xl transition border border-red-100 cursor-pointer"
                    >
                      <DeleteOutlined style={{ fontSize: "18px" }} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {topPosts.length < 5 && (
              <button
                type="button"
                onClick={handleAddPostLink}
                className="flex items-center gap-2 text-emerald-700 font-gilroyBold text-[13px] bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-4 py-2.5 rounded-2xl transition cursor-pointer"
              >
                <PlusOutlined /> Add Another Post Link
              </button>
            )}
          </div>

          <Button
            type="primary"
            onClick={handleSaveData}
            loading={savingData}
            className="w-full h-14 bg-emerald-600 hover:bg-emerald-500 text-white font-gilroyBold text-lg rounded-2xl shadow-lg shadow-emerald-600/25 border-0 cursor-pointer transition-all hover:scale-[1.01] mt-4"
          >
            SAVE & UPDATE AMBASSADOR DATA
          </Button>
        </div>
      </div>

      <AmbassadorActionPlan />
    </div>
  );
};

export default AmbassadorProfileEditor;
